import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { useToast } from '../utils/Toast';
import { supabase } from '../supabaseClient';

type HealthStatus = 'online' | 'server-down' | 'offline' | 'checking';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, '') ?? 'http://localhost:4000';

// Reduced frequency - check every 30 seconds instead of 10
const HEALTH_CHECK_INTERVAL = 30000;
const HEALTH_CHECK_TIMEOUT = 8000;
const MAX_CONSECUTIVE_FAILURES = 3; // Auto-logout after 3 failures (90 seconds)

export const useHealthCheck = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { refreshSession, user } = useAuth();
  
  const [healthStatus, setHealthStatus] = useState<HealthStatus>('online');
  const consecutiveFailuresRef = useRef(0);
  const healthCheckInFlightRef = useRef(false);
  const healthCheckAbortControllerRef = useRef<AbortController | null>(null);
  const intervalRef = useRef<number | null>(null);
  const lastNotificationRef = useRef<HealthStatus>('online');

  const updateHealthStatus = useCallback((status: HealthStatus) => {
    setHealthStatus(status);
    
    // Only show notifications when status actually changes
    if (lastNotificationRef.current === status) {
      return;
    }
    
    lastNotificationRef.current = status;
    
    switch (status) {
      case 'server-down':
        showToast('Servers temporarily unavailable. Checking...', 'warning', 4000);
        break;
      case 'offline':
        showToast('Network connection lost. Checking...', 'error', 4000);
        break;
      case 'online':
        if (lastNotificationRef.current !== 'checking') {
          showToast('Connection restored', 'success', 3000);
        }
        break;
    }
  }, [showToast]);

  const handleAutoLogout = useCallback(async () => {
    try {
      await supabase.auth.signOut();
      await refreshSession();
      showToast('Auto-logout: Unable to maintain server connection', 'error');
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Failed to auto-logout:', error);
      // Force navigation even if signOut fails
      navigate('/', { replace: true });
    }
  }, [refreshSession, showToast, navigate]);

  const checkNetwork = useCallback(async (): Promise<boolean> => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      
      try {
        await fetch('https://1.1.1.1', {
          mode: 'no-cors',
          signal: controller.signal
        });
        return true;
      } finally {
        clearTimeout(timeoutId);
      }
    } catch {
      return false;
    }
  }, []);

  const performHealthCheck = useCallback(async () => {
    if (healthCheckInFlightRef.current) {
      return;
    }

    // Don't run health checks if user is not authenticated
    if (!user) {
      return;
    }

    healthCheckInFlightRef.current = true;
    
    // Abort previous check if still running
    if (healthCheckAbortControllerRef.current) {
      healthCheckAbortControllerRef.current.abort();
    }

    const controller = new AbortController();
    healthCheckAbortControllerRef.current = controller;
    
    const timeoutId = setTimeout(() => controller.abort(), HEALTH_CHECK_TIMEOUT);

    try {
      const response = await fetch(`${API_BASE_URL}/health`, {
        method: 'GET',
        signal: controller.signal,
        cache: 'no-cache'
      });

      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }

      // Success - reset failure counter
      consecutiveFailuresRef.current = 0;
      updateHealthStatus('online');

    } catch (error) {
      if (controller.signal.aborted) {
        return; // Don't count aborted checks as failures
      }

      consecutiveFailuresRef.current += 1;
      
      // Check if we should auto-logout
      if (consecutiveFailuresRef.current >= MAX_CONSECUTIVE_FAILURES) {
        console.warn(`Health check failed ${MAX_CONSECUTIVE_FAILURES} times, auto-logout initiated`);
        await handleAutoLogout();
        return;
      }

      // Determine if it's a network or server issue
      const networkOk = await checkNetwork();
      updateHealthStatus(networkOk ? 'server-down' : 'offline');
      
    } finally {
      clearTimeout(timeoutId);
      if (healthCheckAbortControllerRef.current === controller) {
        healthCheckAbortControllerRef.current = null;
      }
      healthCheckInFlightRef.current = false;
    }
  }, [user, updateHealthStatus, checkNetwork, handleAutoLogout]);

  const startHealthChecks = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Immediate check
    performHealthCheck();
    
    // Set up periodic checks
    intervalRef.current = window.setInterval(performHealthCheck, HEALTH_CHECK_INTERVAL);
  }, [performHealthCheck]);

  const stopHealthChecks = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    
    if (healthCheckAbortControllerRef.current) {
      healthCheckAbortControllerRef.current.abort();
      healthCheckAbortControllerRef.current = null;
    }
    
    consecutiveFailuresRef.current = 0;
    setHealthStatus('online');
  }, []);

  // Auto-start/stop based on user authentication
  useEffect(() => {
    if (user) {
      startHealthChecks();
    } else {
      stopHealthChecks();
    }

    return stopHealthChecks;
  }, [user, startHealthChecks, stopHealthChecks]);

  return {
    healthStatus,
    consecutiveFailures: consecutiveFailuresRef.current,
    startHealthChecks,
    stopHealthChecks
  };
};